import { useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Copy, ExternalLink, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { financeApi } from '@/services/finance'
import { getOrgWalletLabel, orgWalletApi, OrgWallet } from '@/services/org-wallet'
import { getErrorMessage } from '@/lib/api'
import { formatCurrency, formatDate } from '@/lib/utils'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { resolveReturnToPath } from '@/lib/return-navigation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const toNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return 0
  }

  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getWalletLabel = (wallet: OrgWallet) => getOrgWalletLabel(wallet)

const getPaymentMethodLabel = (payment: {
  payment_method?: unknown
  payment_method_label?: string | null
  payment_source_label?: string | null
}) => {
  if (payment.payment_method_label?.trim()) {
    return payment.payment_method_label.trim()
  }
  if (payment.payment_source_label?.trim()) {
    return payment.payment_source_label.trim()
  }

  const method = payment.payment_method
  if (method && typeof method === 'object') {
    const wallet = method as {
      id?: string | number
      label?: string | null
      wallet_name?: string | null
      wallet_variant_name?: string | null
    }
    return wallet.label || wallet.wallet_name || wallet.wallet_variant_name || (wallet.id != null ? `Wallet ${wallet.id}` : '-')
  }

  if (typeof method === 'string' || typeof method === 'number') {
    return String(method)
  }

  return '-'
}

export default function InvoicePaymentsPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>()
  const location = useLocation()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const backPath = resolveReturnToPath(
    location.search,
    withServiceInstanceId('/finance/invoices', serviceInstanceId),
  )

  const [amount, setAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('')
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')

  const walletsQuery = useQuery({
    queryKey: ['invoice-payment-wallets', serviceInstanceId],
    queryFn: () => orgWalletApi.listWallets(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
    staleTime: 0,
  })

  const invoiceQuery = useQuery({
    queryKey: ['invoice-detail', invoiceId, serviceInstanceId ?? 'none'],
    queryFn: () => financeApi.getInvoice(invoiceId!, serviceInstanceId ?? undefined),
    enabled: Boolean(invoiceId),
  })


  const paymentsQuery = useQuery({
    queryKey: ['invoice-payments', invoiceId, serviceInstanceId ?? 'none'],
    queryFn: () => financeApi.getInvoicePayments({ invoice_id: invoiceId!, page_size: 100, service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(invoiceId),
  })

  const createPaymentMutation = useMutation({
    mutationFn: () => {
      const numericAmount = Number(amount)
      if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
        throw new Error('Enter a valid payment amount.')
      }
      if (!paymentMethod.trim()) {
        throw new Error('Select the wallet that received this payment.')
      }
      return financeApi.createInvoicePayment({
        invoice: invoiceId!,
        amount: numericAmount,
        payment_method: paymentMethod.trim(),
        reference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
      })
    },
    onSuccess: () => {
      toast.success('Payment recorded')
      setAmount('')
      setPaymentMethod('')
      setReference('')
      setNotes('')
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoice-detail', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['finance-invoices-list'] })
      queryClient.invalidateQueries({ queryKey: ['finance-invoice-payments-list'] })
      queryClient.invalidateQueries({ queryKey: ['job-invoices'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deletePaymentMutation = useMutation({
    mutationFn: (id: string | number) => financeApi.deleteInvoicePayment(id),
    onSuccess: () => {
      toast.success('Payment deleted')
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoice-detail', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['finance-invoices-list'] })
      queryClient.invalidateQueries({ queryKey: ['finance-invoice-payments-list'] })
      queryClient.invalidateQueries({ queryKey: ['job-invoices'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const payments = paymentsQuery.data?.results ?? []
  const wallets = walletsQuery.data ?? []
  const walletsLoading = walletsQuery.isLoading

  const totals = useMemo(() => {
    const paid = payments.reduce((sum, item) => sum + toNumber(item.amount), 0)
    const grandTotal = toNumber(invoiceQuery.data?.snapshot_grand_total)
    const due = Math.max(grandTotal - paid, 0)
    return { paid, grandTotal, due }
  }, [payments, invoiceQuery.data?.snapshot_grand_total])

  const submittingDisabled = createPaymentMutation.isPending || !invoiceId || !amount || Number(amount) <= 0 || !paymentMethod.trim()
  const paymentUrl = invoiceQuery.data?.payment_url

  const copyPaymentLink = async () => {
    if (!paymentUrl) return

    try {
      await navigator.clipboard.writeText(paymentUrl)
      toast.success('Payment link copied')
    } catch {
      toast.error('Unable to copy payment link')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to={backPath}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Invoice Payments</h1>
            <p className="text-sm text-muted-foreground">
              Invoice: {invoiceQuery.data?.invoice_number || invoiceQuery.data?.invoice_no || invoiceId || '-'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => paymentUrl ? window.open(paymentUrl, '_blank', 'noopener,noreferrer') : undefined}
            disabled={!paymentUrl}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Open payment link
          </Button>
          <Button variant="outline" onClick={() => void copyPaymentLink()} disabled={!paymentUrl}>
            <Copy className="mr-2 h-4 w-4" />
            Copy payment link
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Invoice Amount</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">{formatCurrency(totals.grandTotal)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Paid</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">{formatCurrency(totals.paid)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Balance</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">{formatCurrency(totals.due)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Record Payment</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="Amount"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
            <select
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none"
              value={paymentMethod}
              onChange={e => setPaymentMethod(e.target.value)}
              disabled={walletsLoading}
            >
              <option value="">Select Wallet</option>
              {wallets.map(w => (
                <option key={w.id} value={w.id}>
                  {getWalletLabel(w)}
                </option>
              ))}
            </select>
            <Input
              placeholder="Reference"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
            <Input
              placeholder="Notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
          <div className="mt-3">
            <Button onClick={() => createPaymentMutation.mutate()} disabled={submittingDisabled}>
              <Plus className="mr-2 h-4 w-4" />
              Add Payment
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payments</CardTitle>
        </CardHeader>
        <CardContent>
          {paymentsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : payments.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
              No payments recorded yet.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border/70">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={String(payment.id)}>
                      <TableCell>{payment.payment_date ? formatDate(payment.payment_date) : '-'}</TableCell>
                      <TableCell>{getPaymentMethodLabel(payment)}</TableCell>
                      <TableCell>{payment.reference || '-'}</TableCell>
                      <TableCell>{payment.notes || '-'}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(toNumber(payment.amount))}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => deletePaymentMutation.mutate(payment.id)}
                          disabled={deletePaymentMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Delete</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
